import { z } from "zod";
import { uploadTargetSchema } from "./documents.ts";
import {
  classificationSchemeSchema,
  documentIdSchema,
  documentOriginSchema,
  documentStatusSchema,
  storedDocumentOriginSchema,
} from "./knowledge.ts";
import { conversationIdSchema } from "./projects.ts";
import { userIdSchema } from "./tenancy.ts";

/**
 * Conversation attachments over the API (openspec change `conversation-attachments`): files
 * attached to one conversation as working material. An attachment is a document scoped to its
 * conversation, never to a project: no project list, folder, wiki or knowledge search returns
 * it. Its readers are server-written (the uploader and the `conversation` principal), so the
 * upload request takes no readers.
 */

/** An attachment's ID: the UUID of its document. */
export const attachmentIdSchema = z.uuid().pipe(documentIdSchema);
export type AttachmentId = z.infer<typeof attachmentIdSchema>;

/**
 * Per-message and per-conversation caps (design §8), constants rather than settings. Deleted
 * attachments do not count; `knowledge.maxUploadMb` stays the per-file limit.
 */
export const ATTACHMENT_LIMITS = {
  perMessage: 10,
  perConversation: 20,
  conversationBytes: 200 * 1024 * 1024,
} as const;

export const uploadAttachmentRequestSchema = z.object({
  /** The file name, shown as the attachment title. */
  title: z.string().trim().min(1).max(255),
  mediaType: z.string().min(1).max(255),
  sizeBytes: z.int().min(1),
  /**
   * Defaults to `knowledge.defaultClassification` at the conversation's scope, or to the
   * tenant's private level in a standalone conversation (which refuses any wider level).
   */
  classification: z.string().min(1).max(40).optional(),
  origin: documentOriginSchema.optional(),
});
export type UploadAttachmentRequest = z.infer<typeof uploadAttachmentRequestSchema>;

/**
 * Changes a standalone attachment's origin until a message first references it
 * (standalone-attachments-private D6). Classification never changes.
 */
export const updateAttachmentOriginSchema = z.strictObject({ origin: documentOriginSchema });
export type UpdateAttachmentOrigin = z.infer<typeof updateAttachmentOriginSchema>;

/** An attachment as its readers see it: no readers list, no project, no object key. */
export const attachmentSchema = z.object({
  id: attachmentIdSchema,
  conversationId: conversationIdSchema,
  title: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number().int().min(0),
  status: documentStatusSchema,
  failureReason: z.string().nullable(),
  /** A level id of the tenant scheme, fixed at upload. */
  classification: z.string(),
  origin: storedDocumentOriginSchema,
  /** Whether the level allows downloads, so a client can hide the action. */
  downloadAllowed: z.boolean(),
  uploadedBy: userIdSchema,
  createdAt: z.iso.datetime(),
});
export type Attachment = z.infer<typeof attachmentSchema>;

export const uploadAttachmentResponseSchema = z.object({
  attachment: attachmentSchema,
  upload: uploadTargetSchema,
});
export type UploadAttachmentResponse = z.infer<typeof uploadAttachmentResponseSchema>;

/** Creator-only presentation context. Never copied onto a shared attachment record. */
export const attachmentComposerSchema = z.object({
  version: z.literal(1),
  canUpload: z.boolean(),
  canManage: z.boolean(),
  reason: z
    .enum([
      "archived",
      "project_frozen",
      "attachments_disabled",
      "standalone_disabled",
      "unavailable",
      // standalone-attachments-private: the scheme has no `named`-floor level (fails closed).
      "private_level_unavailable",
    ])
    .optional(),
  /**
   * A standalone conversation: uploads are private only (the tenant's `named`-floor levels), and
   * the client uploads without a classification review. Optional for N-1 compatibility.
   */
  privateOnly: z.literal(true).optional(),
  uploadPolicy: z.object({
    defaultClassification: z.string().min(1),
    /** The complete scheme, so existing attachments keep their labels. */
    classifications: classificationSchemeSchema,
    /** With `privateOnly`: the level ids an upload may use (`named` readers floor). */
    privateLevels: z.array(z.string().min(1)).optional(),
    maxUploadMb: z.number().positive(),
  }),
  /** Complete only for readable IDs in this list; missing entries mean unknown, never unsent. */
  attachmentStates: z.array(z.object({ attachmentId: attachmentIdSchema, sent: z.boolean() })),
});
export type AttachmentComposer = z.infer<typeof attachmentComposerSchema>;

/** Optional for N-1 compatibility. Recipients receive no composer or sent-state information. */
export const attachmentListSchema = z.object({
  attachments: z.array(attachmentSchema),
  composer: attachmentComposerSchema.optional(),
});
export type AttachmentList = z.infer<typeof attachmentListSchema>;

/**
 * UI message part type of a user message's attachments (`data-` + {@link ATTACHMENTS_DATA_NAME}):
 * references only, never content.
 */
export const ATTACHMENTS_DATA_NAME = "attachments";
export const ATTACHMENTS_PART_TYPE = `data-${ATTACHMENTS_DATA_NAME}` as const;

export const attachmentsPartSchema = z.object({
  type: z.literal(ATTACHMENTS_PART_TYPE),
  data: z.object({
    attachments: z
      .array(z.object({ documentId: attachmentIdSchema, title: z.string(), mediaType: z.string() }))
      .max(ATTACHMENT_LIMITS.perMessage),
  }),
});
export type AttachmentsPart = z.infer<typeof attachmentsPartSchema>;
